import type { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { baseApi } from '@shared/api/baseApi';
import type { ApiMessage } from '@shared/api/response';
import type {
  EntityType,
  OwnerOut,
  PageResponse,
  RoleDefaults,
  SyncOut,
  TaskDetail,
  TaskRow,
  TaskSummary,
} from '@shared/api/types';
import { fileNameFrom, type DownloadedFile } from '@shared/lib/download';

export type Scope = 'firm' | 'mine';
export type DeskView = 'all' | 'now' | 'done' | 'archived';
export type Alloc = 'all' | 'unassigned' | 'wip' | 'completed';

/** Everything that narrows the board. `undefined` = not filtered. */
export interface BoardQuery {
  year: number;
  scope: Scope;
  service?: string | undefined;
  entityType?: EntityType | undefined;
  groupId?: string | undefined;
  view: DeskView;
  alloc: Alloc;
  staff?: string | undefined;
  step?: number | undefined;
  q?: string | undefined;
}

export interface BoardListQuery extends BoardQuery {
  page: number;
  pageSize: number;
  sort?: string | undefined;
}

/** Workflow actions can carry files, so they post multipart: a JSON `body` + `files`. */
function multipart(body: object, files: File[]): FormData {
  const form = new FormData();
  form.append('body', JSON.stringify(body));
  for (const f of files) form.append('files', f);
  return form;
}

const params = (q: Partial<BoardQuery>) => {
  const text = q.q?.trim();
  return { ...q, q: text === '' ? undefined : text };
};
const TASK_LIST = { type: 'Task' as const, id: 'LIST' };

const tasksApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listTasks: build.query<PageResponse<TaskRow>, BoardListQuery>({
      query: (q) => ({ url: '/obligations', params: params(q) }),
      providesTags: [TASK_LIST],
    }),
    taskSummary: build.query<TaskSummary, BoardQuery>({
      query: (q) => ({ url: '/obligations/summary', params: params({ ...q, step: undefined }) }),
      providesTags: [TASK_LIST],
    }),
    task: build.query<TaskDetail, string>({
      query: (id) => `/obligations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Task', id }],
    }),
    advanceTask: build.mutation<
      ApiMessage,
      {
        id: string;
        version: number;
        toUserId?: string | null;
        remark?: string;
        acks?: Record<string, string>;
        files: File[];
      }
    >({
      query: ({ id, files, ...body }) => ({
        url: `/obligations/${id}/advance`,
        method: 'POST',
        body: multipart(body, files),
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Task', id }, TASK_LIST, 'Audit'],
    }),
    sendBackTask: build.mutation<
      ApiMessage,
      { id: string; version: number; toStep?: number; remark?: string; files: File[] }
    >({
      query: ({ id, files, ...body }) => ({
        url: `/obligations/${id}/send-back`,
        method: 'POST',
        body: multipart(body, files),
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Task', id }, TASK_LIST, 'Audit'],
    }),
    holdTask: build.mutation<ApiMessage, { id: string; version: number; hold: boolean }>({
      query: ({ id, version, hold }) => ({
        url: `/obligations/${id}/${hold ? 'hold' : 'resume'}`,
        method: 'POST',
        body: { version },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Task', id }, TASK_LIST, 'Audit'],
    }),
    archiveTask: build.mutation<ApiMessage, { id: string; archived: boolean }>({
      query: ({ id, archived }) => ({
        url: `/obligations/${id}/${archived ? 'archive' : 'unarchive'}`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Task', id }, TASK_LIST],
    }),
    exportTasks: build.mutation<DownloadedFile, BoardQuery & { format: 'csv' | 'xlsx' }>({
      query: ({ format, ...q }) => ({
        url: '/obligations/export',
        params: { ...params(q), format },
        responseHandler: (r) => r.blob(),
        cache: 'no-cache',
      }),
      transformResponse: (blob: Blob, meta, { format }) => ({
        blob,
        fileName: fileNameFrom(
          (meta as FetchBaseQueryMeta | undefined)?.response?.headers.get('content-disposition') ?? null,
          `COMPLIANCE-DESK_DASHBOARD.${format}`,
        ),
      }),
    }),
    downloadAttachment: build.mutation<DownloadedFile, { id: string; fileName: string }>({
      query: ({ id }) => ({
        url: `/attachments/${id}`,
        responseHandler: (r) => r.blob(),
        cache: 'no-cache',
      }),
      transformResponse: (blob: Blob, _meta, { fileName }) => ({ blob, fileName }),
    }),
    generateTasks: build.mutation<ApiMessage<SyncOut>, { year: number }>({
      query: ({ year }) => ({ url: '/obligations/generate', method: 'POST', params: { year } }),
      invalidatesTags: [TASK_LIST],
    }),
    firmTeam: build.query<RoleDefaults, undefined>({
      query: () => '/role-defaults',
      providesTags: [{ type: 'RoleDefaults', id: 'FIRM' }],
    }),
    saveFirmTeam: build.mutation<ApiMessage<RoleDefaults>, RoleDefaults>({
      query: (body) => ({ url: '/role-defaults', method: 'PUT', body }),
      invalidatesTags: [{ type: 'RoleDefaults', id: 'FIRM' }, 'Audit'],
    }),
    clientTeam: build.query<RoleDefaults, string>({
      query: (clientId) => `/clients/${clientId}/role-defaults`,
      providesTags: (_r, _e, id) => [{ type: 'RoleDefaults', id }],
    }),
    saveClientTeam: build.mutation<ApiMessage<RoleDefaults>, { clientId: string; body: RoleDefaults }>({
      query: ({ clientId, body }) => ({
        url: `/clients/${clientId}/role-defaults`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { clientId }) => [{ type: 'RoleDefaults', id: clientId }, 'Audit'],
    }),
    setOwner: build.mutation<
      ApiMessage<OwnerOut>,
      { clientId: string; serviceKey: string; year: number; ownerId: string | null }
    >({
      query: ({ clientId, serviceKey, year, ownerId }) => ({
        url: `/profiles/${clientId}/${serviceKey}/${year}/owner`,
        method: 'PUT',
        body: { ownerId },
      }),
      invalidatesTags: ['Task', 'Owner', 'Audit'],
    }),
  }),
});

export const {
  useListTasksQuery,
  useTaskSummaryQuery,
  useTaskQuery,
  useAdvanceTaskMutation,
  useSendBackTaskMutation,
  useHoldTaskMutation,
  useArchiveTaskMutation,
  useExportTasksMutation,
  useDownloadAttachmentMutation,
  useGenerateTasksMutation,
  useFirmTeamQuery,
  useSaveFirmTeamMutation,
  useClientTeamQuery,
  useSaveClientTeamMutation,
  useSetOwnerMutation,
} = tasksApi;
